import styled from "@emotion/styled";

import { BaseLink } from "shared/components/common/base-link";
import { fontRoboto, fontRobotoCondensed, theme } from "shared/lib/styles";

import { BaseImage } from "scmp-app/components/common/base-image";
import { EntityFollowButton } from "scmp-app/components/entity-follow-button";
import { EntityOnelineMenuLite } from "scmp-app/components/entity-oneline-menu-lite";
import {
  StyledSwiper,
  StyledSwiperSlide,
} from "scmp-app/components/entity-oneline-menu-lite/styles";
import { VideoLatestVideo } from "scmp-app/components/video/latest-video";
import { VideoHeaderDropdownMenu } from "scmp-app/components/video/video-header-dropdown-menu";
import { VideoList } from "scmp-app/components/video/video-list";
import { VideoShortCarousel } from "scmp-app/components/video/video-short-carousel";

export const Container = styled.div`
  display: grid;
  grid:
    "header" min-content
    "latest-video" min-content
    "latest-shorts" min-content
    "all-videos" min-content
    / 100%;

  max-inline-size: 1272px;
  margin-inline: auto;
`;

export const Header = styled.div`
  grid-area: header;

  display: grid;
  /* Mobile: 左欄文字佔滿剩餘寬度、右欄 Banner 靠 end，等同 space-between */
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0;

  margin-block-start: 20px;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "banner-image section-name-header" min-content
      "banner-image section-name" min-content
      / auto minmax(0, 1fr);
    gap: 0;
    align-items: start;

    margin-block-start: 32px;
    padding-inline: 32px;
  }
`;

export const BannerImageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;

  min-block-size: 0;

  :empty {
    display: none;
  }

  ${theme.breakpoints.only("mobile")} {
    grid-row: 1 / span 2;
    grid-column: 2;

    place-self: center end;
  }

  ${theme.breakpoints.up("tablet")} {
    grid-area: banner-image;

    inline-size: 100%;
    block-size: 100%;
    padding-inline-end: 32px;
  }
`;

export const BannerImage = styled(BaseImage)`
  inline-size: 100%;
  block-size: auto;
  max-inline-size: 100px;

  object-fit: contain;

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 140px;
  }
`;

export const SectionNameContainer = styled.div`
  ${theme.breakpoints.only("mobile")} {
    display: contents;
  }

  ${theme.breakpoints.up("tablet")} {
    grid-area: section-name;

    display: flex;
    column-gap: 32px;
    align-items: center;
  }
`;

export const OneLineMenuContainer = styled.div`
  grid-area: one-line-menu;

  display: flex;
  justify-content: flex-end;

  margin-block-start: 20px;

  ${theme.breakpoints.up("tablet")} {
    column-gap: 20px;
    align-items: center;

    margin-block-start: 8px;
  }
`;

export const SectionNameHeader = styled(BaseLink)`
  color: #808080;
  font-weight: 400;
  font-size: 14px;
  font-family: ${fontRobotoCondensed};
  font-style: normal;
  line-height: normal;
  text-transform: uppercase;

  ${theme.breakpoints.only("mobile")} {
    grid-row: 1;
    grid-column: 1;

    place-self: start;

    margin-block-end: 4px;
  }

  ${theme.breakpoints.up("tablet")} {
    grid-area: section-name-header;

    font-size: 20px;
  }
`;

export const SectionName = styled.div`
  font-weight: 700;
  font-size: 24px;
  font-family: ${fontRobotoCondensed};
  line-height: 120%;
  text-transform: uppercase;

  ${theme.breakpoints.only("mobile")} {
    grid-row: 2;
    grid-column: 1;

    place-self: start;
  }

  ${theme.breakpoints.up("tablet")} {
    font-size: 44px;
  }
`;

export const StyledEntityFollowButton = styled(EntityFollowButton)`
  display: none;

  ${theme.breakpoints.up("tablet")} {
    display: block;
    align-self: center;
  }

  button {
    inline-size: auto;
    padding-block: 4px 3px;
    padding-inline: 8px;

    font-weight: 400;
    font-size: 14px;
    font-family: ${fontRobotoCondensed};
    line-height: 19.6px;
    white-space: nowrap;
  }
`;

export const StyledEntityOnelineMenuLite = styled(EntityOnelineMenuLite)`
  flex: 1;

  min-inline-size: 0;

  ${StyledSwiper} {
    .swiper-wrapper {
      block-size: 100%;
    }

    ${StyledSwiperSlide}:first-of-type {
      color: #000000;
      font-weight: 700;
      font-size: 14px;
      font-family: ${fontRoboto};
      font-style: normal;
      line-height: 140%;

      ${theme.breakpoints.only("mobile")} {
        display: none;
      }
    }

    ${StyledSwiperSlide}:not(:last-child) {
      margin-inline-end: 20px;
    }
  }

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }
`;

export const StyledVideoHeaderDropdownMenu = styled(VideoHeaderDropdownMenu)`
  ${theme.breakpoints.only("mobile")} {
    grid-row: 3;
    grid-column: 1 / -1;

    inline-size: 100%;
    margin-block-start: 12px;
  }
`;

export const StyledVideoLatestVideo = styled(VideoLatestVideo)`
  grid-area: latest-video;

  margin-block-start: 20px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }
`;

export const StyledVideoShortCarousel = styled(VideoShortCarousel)`
  grid-area: latest-shorts;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-inline: 32px;
  }
`;

export const StyledVideoList = styled(VideoList)`
  grid-area: all-videos;

  margin-block-start: 20px;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-inline: 32px;
  }
`;
